// Create symlink farm for team-ai-pack mount dirs.
//
// Links project/.claude/<dir> → project/.claude/pack/<dir> for each of:
// skills, agents, commands, hooks, workflows, scripts.
//
// Why symlinks vs copy: Claude Code reads .claude/{skills,agents,...} by
// convention. Submodule lives at .claude/pack/. Symlinks let Claude see the
// pack content at expected paths without duplication. When pack updates, all
// projects pick up changes via the symlink.
//
// Conflict handling: if .claude/<dir> exists as a real directory (not symlink),
// caller must pass force=true to backup and replace. Otherwise we return a
// conflict report and let CLI prompt the user.
import { promises as fs } from "node:fs";
import { dirname, join, relative } from "node:path";

import { backupDirBeforeReplace } from "./backup-existing-dir-before-symlink-override.js";
import { buildSymlinkArgsForPlatform } from "./build-symlink-args-for-platform.js";
import { detectHostPlatform } from "./detect-host-platform.js";
import { pathExists } from "./filesystem-helpers.js";

// Mount dirs: Claude Code convention paths + Avatar-specific extras.
// - skills/agents/commands/hooks → Claude Code scan tự động
// - workflows/scripts → Avatar workflow refs + hook companion scripts
// - knowledge → docs team-shared (coding standards, security policy...) — Claude Code
//   không auto-scan nhưng CLAUDE.md + agents reference được qua .claude/knowledge/
export const TEAM_PACK_MOUNT_DIRS = [
  "skills",
  "agents",
  "commands",
  "hooks",
  "workflows",
  "scripts",
  "knowledge",
] as const;

export interface SyncMountedDirResult {
  dir: string;
  action: "created" | "updated" | "skipped-conflict" | "backed-up-and-linked" | "source-missing";
  backupPath?: string;
}

// Check whether a path is an existing symlink. fs.lstat doesn't follow symlinks.
// Windows: junction cũng được lstat báo là symbolic link → nhánh idempotent bên
// dưới dùng chung được cho cả 2 kiểu link.
async function isSymbolicLink(path: string): Promise<boolean> {
  try {
    const st = await fs.lstat(path);
    return st.isSymbolicLink();
  } catch {
    return false;
  }
}

// Tạo link source → dest đúng kiểu của platform đang chạy (Windows dùng junction
// để không cần đặc quyền — xem build-symlink-args-for-platform.ts).
async function createMountLink(source: string, dest: string): Promise<void> {
  const { target, type } = buildSymlinkArgsForPlatform(source, dest, detectHostPlatform());
  await fs.symlink(target, dest, type);
}

// Gỡ link cũ trước khi tạo lại. Trên Windows, gỡ junction/symlink-thư-mục có thể
// ném EPERM/EISDIR/ENOTEMPTY từ unlink → phải rmdir (rmdir chỉ xoá bản thân
// reparse point, KHÔNG đụng nội dung thư mục đích). POSIX không bao giờ rơi vào
// nhánh fallback nên hành vi giữ nguyên.
async function removeExistingLink(dest: string): Promise<void> {
  try {
    await fs.unlink(dest);
  } catch (err) {
    const code = (err as NodeJS.ErrnoException).code;
    if (code !== "EPERM" && code !== "EISDIR" && code !== "ENOTEMPTY") throw err;
    await fs.rmdir(dest);
  }
}

// Sync one mount dir: pack/<dir> → .claude/<dir> (relative symlink).
//
// Cases:
//   1. source doesn't exist     → action: source-missing
//   2. dest doesn't exist       → create symlink, action: created
//   3. dest is existing symlink → recreate (idempotent), action: updated
//   4. dest is real dir, !force → action: skipped-conflict (caller prompts user)
//   5. dest is real dir, force  → backup + create symlink, action: backed-up-and-linked
export async function syncMountedDir(
  source: string,
  dest: string,
  force: boolean,
): Promise<SyncMountedDirResult> {
  const dir = relative(dirname(dest), dest) || dest;

  if (!(await pathExists(source))) {
    return { dir, action: "source-missing" };
  }

  if (await pathExists(dest)) {
    if (await isSymbolicLink(dest)) {
      await removeExistingLink(dest);
    } else if (force) {
      const backupPath = await backupDirBeforeReplace(dest);
      await createMountLink(source, dest);
      return { dir, action: "backed-up-and-linked", backupPath };
    } else {
      return { dir, action: "skipped-conflict" };
    }
  }

  await createMountLink(source, dest);
  return { dir, action: "created" };
}

// Sync all configured mount dirs. Returns aggregated results.
export async function syncAllMountDirs(
  packDir: string,
  claudeDir: string,
  force: boolean,
): Promise<SyncMountedDirResult[]> {
  const results: SyncMountedDirResult[] = [];
  for (const dir of TEAM_PACK_MOUNT_DIRS) {
    const source = join(packDir, dir);
    const dest = join(claudeDir, dir);
    results.push(await syncMountedDir(source, dest, force));
  }
  return results;
}
